import { $, $$, el } from './dom.js';
import { upgradeEmojiInputs } from './emoji-picker.js';
import { ct } from './i18n.js';
import type { RoleOption } from './role-picker.js';

/**
 * Editor for the entries of a role menu.
 *
 * Kept apart from the rest of the page: it is the only genuinely specific part
 * of the Reaction roles module, everything else (dialog, list, saving) being
 * shared through the resource pages.
 *
 * No HTML is built by string concatenation. The old version generated its
 * fields with `innerHTML` and `onclick="removeRole('<role name>')"` attributes:
 * a role named `'); alert(1); //` was enough to run arbitrary script in an
 * administrator's browser.
 */

export type ReactionKind = 'emoji' | 'button' | 'select';

export interface SelectOption {
  label: string;
  description: string;
  emoji: string;
  roleId: string;
}

export interface ReactionEntry {
  kind: ReactionKind;
  emoji: string;
  label: string;
  style: string;
  roleId: string;
  placeholder: string;
  options: SelectOption[];
}

const KIND_LABELS: Record<ReactionKind, string> = {
  emoji: 'Emoji',
  button: 'Button',
  select: 'Dropdown',
};

const BUTTON_STYLES: [value: string, label: string][] = [
  ['secondary', ct('styleGrey', 'Grey')],
  ['primary', ct('styleBlue', 'Blue')],
  ['success', ct('styleGreen', 'Green')],
  ['danger', ct('styleRed', 'Red')],
];

const EMPTY_ENTRY: ReactionEntry = {
  kind: 'emoji',
  emoji: '',
  label: '',
  style: 'secondary',
  roleId: '',
  placeholder: ct('pickRole', 'Pick a role'),
  options: [],
};

export class ReactionEditor {
  constructor(
    private readonly list: HTMLElement,
    private readonly roles: RoleOption[],
    private readonly emptyHint: HTMLElement | null,
  ) {}

  /** Resets the editor with the supplied entries. */
  load(entries: ReactionEntry[]): void {
    this.list.replaceChildren();
    for (const entry of entries) this.add(entry.kind, entry);
    this.refreshHint();
  }

  add(kind: ReactionKind, entry?: ReactionEntry): void {
    const card = this.renderEntry({ ...EMPTY_ENTRY, ...entry, kind });
    this.list.append(card);

    // The entries are created after startup: the emoji picker has to be wired
    // up here, the global boot pass did not see them.
    upgradeEmojiInputs(card);
    this.refreshHint();
  }

  /** Rebuilds the array of entries from the DOM. */
  collect(): ReactionEntry[] {
    return $$('[data-entry]', this.list).map((card) => {
      const kind = (card.dataset.entry ?? 'emoji') as ReactionKind;
      const read = (name: string) =>
        $<HTMLInputElement | HTMLSelectElement>(`[data-field="${name}"]`, card)?.value ?? '';

      if (kind === 'select') {
        return {
          ...EMPTY_ENTRY,
          kind,
          placeholder: read('placeholder') || ct('pickRole', 'Pick a role'),
          options: $$('[data-option]', card).map((row) => ({
            label: $<HTMLInputElement>('[data-field="label"]', row)?.value ?? '',
            description: '',
            emoji: $<HTMLInputElement>('[data-field="emoji"]', row)?.value ?? '',
            roleId: $<HTMLSelectElement>('[data-field="roleId"]', row)?.value ?? '',
          })),
        };
      }

      return {
        ...EMPTY_ENTRY,
        kind,
        emoji: read('emoji'),
        label: read('label'),
        style: read('style') || 'secondary',
        roleId: read('roleId'),
      };
    });
  }

  private refreshHint(): void {
    this.emptyHint?.classList.toggle('hidden', $$('[data-entry]', this.list).length > 0);
  }

  /** Dropdown of the roles; those out of the bot's reach are disabled. */
  private roleSelect(selected: string): HTMLSelectElement {
    const select = el('select', {
      class: 'select select-bordered select-sm w-full',
    });
    select.append(
      el('option', { text: ct('chooseRole', '- Pick a role -'), attrs: { value: '' } }),
    );

    for (const role of this.roles) {
      const option = el('option', {
        text: `@${role.name}${role.aboveBot ? ` (${ct('outOfReach', 'out of reach')})` : ''}`,
        attrs: { value: role.id },
      });
      option.selected = role.id === selected;
      option.disabled = Boolean(role.aboveBot);
      select.append(option);
    }

    return select;
  }

  private textInput(options: {
    value: string;
    field: string;
    placeholder: string;
    maxlength: number;
    className: string;
  }): HTMLInputElement {
    const input = el('input', {
      class: `input input-bordered input-sm ${options.className}`,
      attrs: {
        type: 'text',
        placeholder: options.placeholder,
        maxlength: String(options.maxlength),
      },
    });
    input.value = options.value;
    input.dataset.field = options.field;

    if (options.field === 'emoji') input.dataset.emojiInput = 'true';
    return input;
  }

  private iconButton(label: string, ariaLabel: string, onClick: () => void): HTMLButtonElement {
    const button = el('button', {
      class: 'btn btn-ghost btn-xs',
      text: label,
      attrs: { type: 'button', 'aria-label': ariaLabel },
    });
    button.addEventListener('click', onClick);
    return button;
  }

  private renderEntry(entry: ReactionEntry): HTMLElement {
    const card = el('div', {
      class: 'border-base-300 space-y-3 rounded-lg border p-3',
    });
    card.dataset.entry = entry.kind;

    const header = el('div', {
      class: 'flex items-center justify-between gap-2',
    });
    header.append(el('span', { class: 'badge badge-sm', text: KIND_LABELS[entry.kind] }));
    header.append(
      this.iconButton(ct('removeReaction', 'Remove'), ct('removeReaction', 'Remove'), () => {
        card.remove();
        this.refreshHint();
      }),
    );
    card.append(header);

    card.append(entry.kind === 'select' ? this.renderSelect(entry) : this.renderSimple(entry));
    return card;
  }

  /** Emoji or button entry: a single role. */
  private renderSimple(entry: ReactionEntry): HTMLElement {
    const row = el('div', { class: 'flex flex-wrap gap-2' });

    row.append(
      this.textInput({
        value: entry.emoji,
        field: 'emoji',
        placeholder: '🙂',
        maxlength: 64,
        className: 'w-24',
      }),
    );

    if (entry.kind === 'button') {
      row.append(
        this.textInput({
          value: entry.label,
          field: 'label',
          placeholder: ct('buttonLabelPh', 'Button label'),
          maxlength: 80,
          className: 'min-w-32 flex-1',
        }),
      );

      const style = el('select', {
        class: 'select select-bordered select-sm w-28',
      });
      for (const [value, label] of BUTTON_STYLES) {
        const option = el('option', { text: label, attrs: { value } });
        option.selected = entry.style === value;
        style.append(option);
      }
      style.dataset.field = 'style';
      row.append(style);
    }

    const select = this.roleSelect(entry.roleId);
    select.classList.add('min-w-40', 'flex-1');
    select.dataset.field = 'roleId';
    row.append(select);

    return row;
  }

  /** Dropdown entry: one role per option. */
  private renderSelect(entry: ReactionEntry): HTMLElement {
    const container = el('div', { class: 'space-y-3' });

    container.append(
      this.textInput({
        value: entry.placeholder || ct('pickRole', 'Pick a role'),
        field: 'placeholder',
        placeholder: ct('selectPrompt', 'Text shown on the dropdown'),
        maxlength: 150,
        className: 'w-full',
      }),
    );

    const optionsBox = el('div', { class: 'space-y-2' });
    container.append(optionsBox);

    const addOption = (option: SelectOption) => {
      const row = el('div', { class: 'flex flex-wrap gap-2' });
      row.dataset.option = 'true';

      row.append(
        this.textInput({
          value: option.label,
          field: 'label',
          placeholder: ct('optionLabel', 'Label'),
          maxlength: 100,
          className: 'min-w-32 flex-1',
        }),
        this.textInput({
          value: option.emoji,
          field: 'emoji',
          placeholder: '🙂',
          maxlength: 64,
          className: 'w-20',
        }),
      );

      const select = this.roleSelect(option.roleId);
      select.classList.add('min-w-40', 'flex-1');
      select.dataset.field = 'roleId';
      row.append(select);

      row.append(
        this.iconButton('✕', ct('removeOption', 'Remove this option'), () => row.remove()),
      );
      optionsBox.append(row);
      upgradeEmojiInputs(row);
    };

    const initial =
      entry.options.length > 0
        ? entry.options
        : [{ label: '', description: '', emoji: '', roleId: '' }];
    for (const option of initial) addOption(option);

    const addButton = el('button', {
      class: 'btn btn-outline btn-xs',
      text: ct('addOption', '+ Add an option'),
      attrs: { type: 'button' },
    });
    addButton.addEventListener('click', () =>
      addOption({ label: '', description: '', emoji: '', roleId: '' }),
    );
    container.append(addButton);

    return container;
  }
}
